import { FileUploadResponse } from '../../../../api/core'
import { MarketArchetypeUnacceptedEventType } from '../../../api/markets'

/** An active event on a game blueprint of this Market Level Blueprint whose type it does not accept. */
export type OddCaseEvent = {
  eventId: string
  eventName: string
  typeId: string
  gameId: string
  gameName: string
  /** A store icon URL, or an uploaded icon the icon component reads with the user's credentials. */
  gameIcon?: string | FileUploadResponse
  blueprintId: string
  blueprintName: string
  /** An analyst marked the event as a legitimate exception, so it no longer needs review. */
  accepted: boolean
}

export type OddCaseTypeGroup = {
  typeId: string
  events: OddCaseEvent[]
}

/** How often an event has run on its game blueprint, from that blueprint's evolution. */
export type OddCaseEventHistory = {
  occurrences: number
  /** The latest occurrence, absent when the evolution reported no dates. */
  lastSeenTimestamp?: number
}

/** Histories per game blueprint, then per event. */
export type OddCaseHistoryByBlueprint = { [blueprintId: string]: { [eventId: string]: OddCaseEventHistory } }

type NameLookup = {
  gameNames: { [gameId: string]: string }
  gameIcons?: { [gameId: string]: string | FileUploadResponse | undefined }
  blueprintNames: { [blueprintId: string]: string }
}

const byGameThenName = (a: OddCaseEvent, b: OddCaseEvent) =>
  a.gameName.localeCompare(b.gameName) || a.eventName.localeCompare(b.eventName) || a.eventId.localeCompare(b.eventId)

/**
 * Turns the archetype's unaccepted types into the card's groups, naming the games and game
 * blueprints, and picking the game icons, from what the page already loaded.
 *
 * The groups keep the endpoint's order, most events first.
 */
export const oddCaseGroups = (
  unacceptedEventTypes: MarketArchetypeUnacceptedEventType[] | undefined,
  { gameNames, gameIcons, blueprintNames }: NameLookup
): OddCaseTypeGroup[] =>
  (unacceptedEventTypes || []).map((eventType) => ({
    typeId: eventType.typeId,
    events: eventType.events
      .map((event) => ({
        eventId: event.eventId,
        eventName: event.name,
        typeId: eventType.typeId,
        gameId: event.gameId,
        gameName: gameNames[event.gameId] || '',
        gameIcon: gameIcons?.[event.gameId],
        blueprintId: event.blueprintId,
        blueprintName: blueprintNames[event.blueprintId] || '',
        accepted: event.accepted === true,
      }))
      .sort(byGameThenName),
  }))

const keepEvents = (groups: OddCaseTypeGroup[], keep: (event: OddCaseEvent) => boolean): OddCaseTypeGroup[] =>
  groups.map((group) => ({ ...group, events: group.events.filter(keep) })).filter((group) => group.events.length > 0)

/**
 * Splits the odd cases into the events still to review and the ones accepted as exceptions. A type
 * whose events are all on one side only appears on that side.
 */
export const splitAcceptedOddCases = (groups: OddCaseTypeGroup[]): { toReview: OddCaseTypeGroup[]; accepted: OddCaseTypeGroup[] } => ({
  toReview: keepEvents(groups, (event) => !event.accepted),
  accepted: keepEvents(groups, (event) => event.accepted),
})

/**
 * Whether the event at this position starts a run of events from the same game. The events are
 * listed by game, so the card names a game once, on the first of its rows.
 */
export const startsGameRun = (events: OddCaseEvent[], index: number): boolean => index === 0 || events[index - 1]?.gameId !== events[index]?.gameId

/** The game blueprints the groups' events sit on, each once. */
export const oddCaseBlueprintIds = (groups: OddCaseTypeGroup[]): string[] =>
  Array.from(new Set(groups.flatMap((group) => group.events.map((event) => event.blueprintId))))

/**
 * Counts the occurrences of every event in a game blueprint's evolution, which lists one entry per
 * time an event ran.
 */
export const eventHistoryFromEvolution = (evolution: { eventId: string; start?: number }[]): { [eventId: string]: OddCaseEventHistory } =>
  evolution.reduce((histories: { [eventId: string]: OddCaseEventHistory }, entry) => {
    const current = histories[entry.eventId] || { occurrences: 0 }
    const lastSeenTimestamp = entry.start === undefined ? current.lastSeenTimestamp : Math.max(entry.start, current.lastSeenTimestamp ?? entry.start)
    histories[entry.eventId] = { occurrences: current.occurrences + 1, lastSeenTimestamp }
    return histories
  }, {})

/**
 * The event's history, once its game blueprint's evolution is in. An event that never ran — one with
 * no durations — is missing from the evolution rather than listed with none.
 */
export const historyOf = (event: OddCaseEvent, histories: OddCaseHistoryByBlueprint): OddCaseEventHistory | undefined => {
  const blueprintHistories = histories[event.blueprintId]
  if (!blueprintHistories) return undefined
  return blueprintHistories[event.eventId] || { occurrences: 0 }
}

/** The latest occurrence of any event in the group, once any of their histories is in. */
export const groupLastSeen = (group: OddCaseTypeGroup, histories: OddCaseHistoryByBlueprint): number | undefined =>
  group.events.reduce<number | undefined>((latest, event) => {
    const seen = historyOf(event, histories)?.lastSeenTimestamp
    return seen === undefined ? latest : Math.max(seen, latest ?? seen)
  }, undefined)

/**
 * Narrows the groups to the events matching a search, by game, event or game blueprint name.
 *
 * A type with no matching event drops out entirely, so the card can hide it while a filter is on.
 */
export const filterOddCaseGroups = (groups: OddCaseTypeGroup[], query: string): OddCaseTypeGroup[] => {
  const needle = query.trim().toLowerCase()
  if (!needle) return groups

  const matches = (event: OddCaseEvent) => [event.gameName, event.eventName, event.blueprintName].some((field) => field.toLowerCase().includes(needle))

  return keepEvents(groups, matches)
}
